// ---------------------------------------------------------------------------
// Service-worker update signal
//
// `registerSW` runs in main.tsx, outside the React tree. This store carries
// its "a new version is waiting" signal into the tree, where
// `ServiceWorkerUpdateToast` renders it. The store holds one boolean and the
// apply callback the registration handed over; `useSyncExternalStore` reads
// it. In dev, in tests, and in the harness the store is never signalled.
// ---------------------------------------------------------------------------

export interface ServiceWorkerUpdateSnapshot {
    /** A newer worker is installed and waiting for the user's go-ahead. */
    readonly updateReady: boolean;
}

type Listener = () => void;

const IDLE: ServiceWorkerUpdateSnapshot = { updateReady: false };
const READY: ServiceWorkerUpdateSnapshot = { updateReady: true };

let snapshot: ServiceWorkerUpdateSnapshot = IDLE;
let applyUpdate: (() => void) | null = null;
const listeners = new Set<Listener>();

function emit(): void {
    for (const listener of Array.from(listeners)) {
        listener();
    }
}

export const serviceWorkerUpdates = {
    subscribe(listener: Listener): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },
    getSnapshot(): ServiceWorkerUpdateSnapshot {
        return snapshot;
    },
    /**
     * Called by the registration's `onNeedRefresh`. `apply` tells the
     * waiting worker to take over; the registration reloads the page once
     * the new worker controls it.
     */
    signalUpdateReady(apply: () => void): void {
        applyUpdate = apply;
        if (snapshot.updateReady) return;
        snapshot = READY;
        emit();
    },
    /** The user chose to reload now. */
    applyUpdate(): void {
        applyUpdate?.();
    },
    /** The user dismissed the toast; the worker keeps waiting for the next load. */
    dismiss(): void {
        if (!snapshot.updateReady) return;
        snapshot = IDLE;
        emit();
    },
    /** Test seam. */
    reset(): void {
        snapshot = IDLE;
        applyUpdate = null;
        listeners.clear();
    },
};
